import { useQuery } from "@apollo/client";
import { useState, useMemo, ReactElement } from "react";
import {
  useQueryParam,
  NumberParam,
  StringParam,
  withDefault,
} from "use-query-params";

import {
  CreateLinkShortenerDomainMutation,
  CreateLinkShortenerDomainMutationVariables,
  CreateLinkShortenerDomainRequestMutation,
  CreateLinkShortenerDomainRequestMutationVariables,
  LinkShortenerDomainStatus,
  ListLinkShortenerDomainsAndRequestsQuery,
} from "~frontend/__generated__/graphql";
import ErrorCard from "~frontend/components/ErrorCard";
import Loading from "~frontend/components/Loading";
import { TableBox, TableControls } from "~frontend/components/Table";
import {
  CREATE_LINK_SHORTENER_DOMAIN,
  CREATE_LINK_SHORTENER_DOMAIN_REQUEST,
  LIST_DOMAINS_AND_REQUESTS,
} from "~frontend/graphql/domains";
import { useCurrentUserIsStaff } from "~frontend/providers/LoggedInUserProvider";
import useMutation from "~frontend/util/useMutation";

import AddDomainDialog from "./AddDomainDialog";
import DomainsTable from "./DomainsTable";
import RequestDomainDialog from "./RequestDomainDialog";
import UpdateDomainDialog from "./UpdateDomainDialog";
import {
  DOMAIN_FILTERS,
  DOMAIN_SORTS,
  DomainFilter,
  DomainSort,
} from "./constants";
import { DomainOrRequest, DomainRequest } from "./types";

function validateSort(sort: string): DomainSort {
  return DOMAIN_SORTS.find(s => s.key === sort)?.key ?? DOMAIN_SORTS[0].key;
}

function validateFilter(filter: string): DomainFilter {
  return (
    DOMAIN_FILTERS.find(s => s.key === filter)?.key ?? DOMAIN_FILTERS[0].key
  );
}

export default function DomainsList(): ReactElement {
  const [page, setPage] = useQueryParam("page", withDefault(NumberParam, 0));
  const [pageSize, setPageSize] = useQueryParam(
    "page_size",
    withDefault(NumberParam, 10)
  );
  const [sort, setSort] = useQueryParam(
    "sort",
    withDefault(StringParam, DOMAIN_SORTS[0].key)
  );

  const [filter, setFilter] = useQueryParam(
    "filter",
    withDefault(StringParam, DOMAIN_FILTERS[0].key)
  );

  const validatedSort = validateSort(sort);
  const validatedFilter = validateFilter(filter);

  const { loading, error, data } =
    useQuery<ListLinkShortenerDomainsAndRequestsQuery>(
      LIST_DOMAINS_AND_REQUESTS,
      {
        pollInterval: 30 * 1000,
      }
    );

  const [addDomainDialogOpen, toggleAddDomainDialog] = useState<{
    open: boolean;
    fulfillingRequestId?: string;
  }>({
    open: false,
  });

  const [requestDomainDialogOpen, toggleRequestDomainDialog] = useState(false);

  const [updateDomainDialogOpenFor, setUpdateDomainDialogFor] = useState<
    null | string
  >(null);

  const isStaff = useCurrentUserIsStaff();

  const [addDomain] = useMutation<
    CreateLinkShortenerDomainMutation,
    CreateLinkShortenerDomainMutationVariables
  >(CREATE_LINK_SHORTENER_DOMAIN, {
    refetchQueries: [LIST_DOMAINS_AND_REQUESTS],
    awaitRefetchQueries: true,
  });

  const [requestDomain] = useMutation<
    CreateLinkShortenerDomainRequestMutation,
    CreateLinkShortenerDomainRequestMutationVariables
  >(CREATE_LINK_SHORTENER_DOMAIN_REQUEST, {
    refetchQueries: [LIST_DOMAINS_AND_REQUESTS],
    awaitRefetchQueries: true,
  });

  const filteredDomains = useMemo(() => {
    const allDomains = data?.listLinkShortenerDomains ?? [];

    // filter
    const filteredDomains =
      validatedFilter === "active"
        ? allDomains.filter(
            domain => domain.status !== LinkShortenerDomainStatus.Archived
          )
        : allDomains.filter(
            domain => domain.status === LinkShortenerDomainStatus.Archived
          );

    // sort
    const domainsAndRequests: DomainOrRequest[] = filteredDomains.sort(
      (a, b) => {
        switch (validatedSort) {
          case "created_at_asc":
            return a.createdAt - b.createdAt;
          case "created_at_desc":
            return b.createdAt - a.createdAt;
          case "domain_asc":
            return a.domain.localeCompare(b.domain);
          case "domain_desc":
            return b.domain.localeCompare(a.domain);
        }
      }
    );

    // add requests at the top
    if (validatedFilter === "active") {
      domainsAndRequests.unshift(
        ...(data?.listPendingLinkShortenerDomainRequests ?? [])
      );
    }

    return domainsAndRequests;
  }, [data, validatedSort, validatedFilter]);

  const rows = filteredDomains.slice(page * pageSize, (page + 1) * pageSize);

  if (error) {
    return <ErrorCard errors={[error]} />;
  }

  async function handleAddDomain(
    values: CreateLinkShortenerDomainMutationVariables
  ) {
    const { errors, data } = await addDomain({
      variables: values,
    });
    if (errors) {
      throw errors;
    } else {
      toggleAddDomainDialog({ open: false });
      setUpdateDomainDialogFor(data?.createLinkShortenerDomain.id ?? null);
    }
  }

  async function handleRequestDomain(
    values: CreateLinkShortenerDomainRequestMutationVariables
  ) {
    const { errors } = await requestDomain({
      variables: values,
    });
    if (errors) {
      throw errors;
    } else {
      toggleRequestDomainDialog(false);
    }
  }

  let fulfillingRequest: null | DomainRequest = null;
  if (addDomainDialogOpen.fulfillingRequestId) {
    fulfillingRequest =
      data?.listPendingLinkShortenerDomainRequests.find(
        r => r.id === addDomainDialogOpen.fulfillingRequestId
      ) ?? null;
  }

  return (
    <>
      <TableControls
        addButtons={[
          {
            label: "Request Domain",
            onClick: () => toggleRequestDomainDialog(true),
            id: "request-domain",
          },
          ...(isStaff
            ? [
                {
                  label: "Import Domain",
                  onClick: () =>
                    toggleAddDomainDialog({
                      open: true,
                    }),
                  id: "add-domain",
                },
              ]
            : []),
        ]}
        sort={{
          value: sort,
          onChange: value => setSort(value as DomainSort),
          options: DOMAIN_SORTS.map(sort => ({
            value: sort.key,
            label: sort.label,
          })),
        }}
        filters={[
          {
            id: "status",
            label: "Status",
            options: DOMAIN_FILTERS.map(filter => ({
              value: filter.key,
              label: filter.label,
            })),
            value: validatedFilter,
            onChange: (value: string) => {
              setPage(0);
              setFilter(value as DomainFilter);
            },
          },
        ]}
      />
      <TableBox sx={{ pb: 1 }}>
        {loading ? (
          <Loading />
        ) : (
          <DomainsTable
            rows={rows}
            handleChangePage={setPage}
            handleChangeRowsPerPage={newRowsPerPage => {
              setPageSize(newRowsPerPage);
              setPage(0);
            }}
            rowsPerPage={pageSize}
            page={page}
            totalCount={filteredDomains.length}
            handleEditDomain={setUpdateDomainDialogFor}
            handleFulfillRequest={requestId => {
              toggleAddDomainDialog({
                open: true,
                fulfillingRequestId: requestId,
              });
            }}
          />
        )}
      </TableBox>
      <AddDomainDialog
        open={addDomainDialogOpen.open}
        onClose={() =>
          toggleAddDomainDialog({
            open: false,
          })
        }
        fulfillingRequest={fulfillingRequest}
        onSubmit={handleAddDomain}
      />
      <UpdateDomainDialog
        open={updateDomainDialogOpenFor !== null}
        onClose={() => setUpdateDomainDialogFor(null)}
        domain={
          updateDomainDialogOpenFor
            ? (data?.listLinkShortenerDomains?.find(
                d => d.id === updateDomainDialogOpenFor
              ) ?? null)
            : null
        }
      />
      <RequestDomainDialog
        open={requestDomainDialogOpen}
        onClose={() => toggleRequestDomainDialog(false)}
        onSubmit={handleRequestDomain}
      />
    </>
  );
}
